import { useState, useEffect, useCallback } from 'react'

const STORAGE_KEY = 'peerpass_theme'

type Theme = 'light' | 'dark' | 'system'

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    // localStorage unavailable
  }
  return 'system'
}

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const isDark = theme === 'dark' || (theme === 'system' && getSystemTheme() === 'dark')
  root.classList.toggle('dark', isDark)
}

/**
 * Hook for manual dark/light/system theme toggle.
 * Persists choice to localStorage and applies it to the <html> element.
 *
 * Usage:
 *   const { theme, setTheme, isDark } = useDarkMode()
 *
 *   // Three-way toggle button:
 *   <button onClick={() => setTheme(isDark ? 'light' : 'dark')}>
 *     {isDark ? <SunIcon /> : <MoonIcon />}
 *   </button>
 */
export function useDarkMode() {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme)

  // Apply theme on mount and when changed
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  // Listen for system theme changes when in 'system' mode
  useEffect(() => {
    if (theme !== 'system') return

    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => applyTheme('system')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme)
    try {
      localStorage.setItem(STORAGE_KEY, newTheme)
    } catch {
      // localStorage unavailable
    }
  }, [])

  const isDark =
    theme === 'dark' || (theme === 'system' && getSystemTheme() === 'dark')

  return { theme, setTheme, isDark }
}
